import { ONE_SECOND, translate } from '@lucidlink/uicore';
import { sendChunkThroughSwInternalChannel, sendWithStallCheck, } from '../../../file-contexts-common';
import { DOWNLOAD_FAILURE_REASONS, DOWNLOAD_START_FAILURE_STAGES, DOWNLOAD_TOAST_IDS, MainToSwBroadcastMessageTypes, ZERO_BYTES_BIG_INT, } from '../../constants';
import { postMessageToSwOnBroadcastChannel } from '../communication';
import { generateDownloadUrl, initiateDownloadViaIframe, } from '../download-link';
import { trackPageActivity } from '../page-activity';
import { secondsSince, toErrorMessage } from '../telemetry';
import { getFailureReason } from './failure-reason.service';
let runningDownloadsCount = 0;
export const initiateDownload = async ({ abortSignal, cleanUp, descriptor, env, hasReadFileContent, report, source, sw, }) => {
    const { artifact, copy } = descriptor;
    const { pingManager, scope, swBroadcastChannel } = sw;
    env.addActiveDownload({ ...artifact, percentDone: 0, scope });
    report.start();
    const startToastId = `${DOWNLOAD_TOAST_IDS.STARTED}-${artifact.id}-${scope}`;
    const finishToastId = `${DOWNLOAD_TOAST_IDS.FINISHED}-${artifact.id}-${scope}`;
    const failedToastId = `${DOWNLOAD_TOAST_IDS.FAILED}-${artifact.id}-${scope}`;
    env.addToast({
        id: startToastId,
        'data-testid': DOWNLOAD_TOAST_IDS.STARTED,
        kind: 'informative',
        content: {
            message: translate('Make sure to keep your tab open until download is complete.'),
            label: translate('Download in progress'),
        },
        hasCloseButton: true,
    });
    pingManager.start();
    const cleanUpIframe = initiateDownloadViaIframe(generateDownloadUrl({
        fsEntryId: artifact.id,
        fsEntryName: artifact.name,
        fsEntrySize: artifact.size,
        isUnifiedDownload: true,
        scope,
    }));
    const { sendChunk, sendEnd } = sendChunkThroughSwInternalChannel(sw.registration);
    const downloadStartedAt = Date.now();
    const pageActivity = trackPageActivity();
    runningDownloadsCount++;
    let sentBytes = ZERO_BYTES_BIG_INT;
    let chunksAckedCount = 0;
    let lastChunkAckAt = downloadStartedAt;
    let isSending = false;
    let isSendingEnd = false;
    const reportFailure = (error, failureReason) => {
        report.fail(source.transferFacts(sentBytes), secondsSince(downloadStartedAt), error, failureReason, {
            chunksAckedCount,
            concurrentDownloadsCount: runningDownloadsCount,
            hiddenDurationSeconds: pageActivity.hiddenDurationSeconds(),
            maxPingGapSeconds: pingManager.getMaxAcknowledgeGap() / ONE_SECOND,
            secondsSinceLastChunkAck: secondsSince(lastChunkAckAt),
            wasOffline: pageActivity.wasOffline(),
        });
        env.addToast({
            id: failedToastId,
            'data-testid': DOWNLOAD_TOAST_IDS.FAILED,
            kind: 'negative',
            dismissTempo: 'never',
            content: {
                message: copy.failed,
                label: translate('Download failed'),
            },
            hasCloseButton: true,
        });
    };
    try {
        for await (const chunk of source.chunks) {
            abortSignal.throwIfAborted();
            isSending = true;
            await sendWithStallCheck(() => sendChunk(chunk), {
                isRecipientAlive: pingManager.isPeerResponsive,
                signal: abortSignal,
            });
            isSending = false;
            chunksAckedCount++;
            lastChunkAckAt = Date.now();
            sentBytes += BigInt(chunk.byteLength);
            // For folder download no percentage is updated
            env.updateActiveDownloadPercentDone(scope, sentBytes);
        }
        isSending = true;
        isSendingEnd = true;
        await sendWithStallCheck(sendEnd, {
            isRecipientAlive: pingManager.isPeerResponsive,
            signal: abortSignal,
        });
        env.removeToast(startToastId);
        env.addToast({
            id: finishToastId,
            'data-testid': DOWNLOAD_TOAST_IDS.FINISHED,
            kind: 'positive',
            content: { message: copy.completed },
            hasCloseButton: true,
        });
        report.complete(source.transferFacts(sentBytes), secondsSince(downloadStartedAt));
    }
    catch (error) {
        env.removeToast(startToastId);
        if (sw.isCancelledByBrowser()) {
            reportFailure(new Error(`Download cancelled by the browser: ${toErrorMessage(error)}`, { cause: error }), DOWNLOAD_FAILURE_REASONS.BROWSER_CANCELLED);
            return;
        }
        if (hasReadFileContent()) {
            reportFailure(error, getFailureReason({
                abortSignal,
                error,
                isPeerResponsive: pingManager.isPeerResponsive,
                isSending,
                isSendingEnd,
            }));
        }
        else {
            report.startFail(error, DOWNLOAD_START_FAILURE_STAGES.DOWNLOAD_START);
        }
        postMessageToSwOnBroadcastChannel(swBroadcastChannel, [
            MainToSwBroadcastMessageTypes.ERROR,
        ]);
        // eslint-disable-next-line no-console -- Let's see how to handle
        console.error('Error in main thread:', error);
    }
    finally {
        runningDownloadsCount--;
        pageActivity.stop();
        await cleanUp();
        pingManager.stop();
        swBroadcastChannel.close();
        env.removeActiveDownload(scope);
        cleanUpIframe();
    }
};
